import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {Link} from "react-router-dom";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faClipboardCheck,
  faCog,
  faExclamation,
  faLock,
  faStar as faStarFilled
} from "@fortawesome/free-solid-svg-icons";
import {faStar} from "@fortawesome/free-regular-svg-icons";

// Contexts
import {AuthContext} from "../../contexts/auth.js";
import {SettingsContext} from "../../contexts/settings.js";
import {useSocket} from "../../contexts/socket.js";
import {NavContext} from "../../contexts/nav.js";
import {GlobalContext} from "../../contexts/global.js";

// Hooks
import useMountedState from "../../hooks/useMountedState.js";

// Utils
import {getWithExpiry, prettyDateInUserTimezone, slugify} from "../../utils/helpers.js";
// import {getIcon} from "../schedule/helpers.js";

// Components
import Badge from "../../components/Badge.js";

// Style
import {flex, z} from "../../style/components/mixins.js";
import {voice} from "../../style/components/typography.js";
import {pulse, slideLeft} from "../../style/components/animations.js";
import {pad, border, transition, colors, shadow} from "../../style/components/variables.js";
import {Text, Small, Inline, Button, Heading, Abbr} from "../../style/components/general.js";

// Socket Constants
import {STATE_ON, STATE_OFF} from "../general/Room.js";

const FacilityCard = ({
  facility,
  setTarget,
  setShowModalSettings,
  setShowModalFavorite,
  showArchived = false,
  favorites,
  initialStatesBySlug,
  locked
}) => {
  const {
    id,
    slug,
    name,
    type,
    types,
    region,
    isDeleted,
    updatedBy,
    updatedAt,
    checksheetTotal,
    checksheetsDue,
    checksheetDrafts,
    checksheetsOverdue,
    hasNotifications
    // eventAlerts
  } = facility;

  const isMounted = useMountedState();

  const {settings} = useContext(SettingsContext);
  const {allStates} = useContext(NavContext);
  const {roleCanAccessPage, roleCanAccessResource, getRolesDefaultPageFrom, currentUser} =
    useContext(AuthContext);
  const {deletingFacilities} = useContext(GlobalContext);

  const socket = useSocket();

  const [statesBySlug, setStatesBySlug] = useState({});

  const typeSlugs = useMemo(() => {
    if (showArchived) return [slug];
    const fromTypes = types?.map(t => t.slug).filter(Boolean);
    return fromTypes?.length ? fromTypes : [slug];
  }, [showArchived, types, slug]);

  const facilityStateOn = useCallback(
    payload => {
      if (!payload.facility || !payload.state || !typeSlugs.includes(payload.facility)) return;
      setStatesBySlug(prev => {
        const existing = prev[payload.facility] || [];
        if (existing.includes(payload.state)) return prev;
        return {...prev, [payload.facility]: [...existing, payload.state]};
      });
    },
    [typeSlugs]
  );

  const facilityStateOff = useCallback(
    payload => {
      if (!payload.facility || !payload.state || !typeSlugs.includes(payload.facility)) return;
      setStatesBySlug(prev => ({
        ...prev,
        [payload.facility]: (prev[payload.facility] || []).filter(state => state !== payload.state)
      }));
    },
    [typeSlugs]
  );

  // Socket Management
  useEffect(() => {
    socket.on(STATE_ON, facilityStateOn);
    socket.on(STATE_OFF, facilityStateOff);

    return () => {
      // unbind all event handlers used in this component
      socket.off(STATE_ON, facilityStateOn);
      socket.off(STATE_OFF, facilityStateOff);
    };
  }, [socket, facilityStateOn, facilityStateOff]);

  useEffect(() => {
    if (isMounted() && initialStatesBySlug) setStatesBySlug(initialStatesBySlug);
  }, [isMounted, initialStatesBySlug]);

  const typeRows = useMemo(() => {
    if (showArchived) return [{name: type, slug}];
    if (types?.length) return types;
    return [{name: type, slug}];
  }, [showArchived, types, type, slug]);

  const savedSlug = useMemo(() => {
    if (showArchived) return null;
    const saved = getWithExpiry(`${slugify(facility.name)}.link`);
    const slugs = types?.map(({slug: s}) => s) || [];
    if (!slugs?.includes(saved)) return null;
    return saved;
  }, [facility, types, showArchived]);

  const link = useMemo(
    () =>
      roleCanAccessPage("facility_dashboard")
        ? `/facilities/${savedSlug || slug}`
        : getRolesDefaultPageFrom("facility_dashboard", savedSlug || slug),
    [savedSlug, slug, getRolesDefaultPageFrom, roleCanAccessPage]
  );

  const heading = useRef();

  const deleting = useMemo(() => deletingFacilities[id] === false, [deletingFacilities, id]);
  const deleteSuccessful = useMemo(() => deletingFacilities[id] === true, [deletingFacilities, id]);

  return (
    <Card
      $showMarquee={heading.current?.offsetWidth < heading.current?.scrollWidth}
      $locked={locked || deleting}
      $deleting={deleting}>
      {locked && !deleting && (
        <OverlayWrapper>
          <OverlayText>
            <FontAwesomeIcon icon={faLock} /> FOR MAINTENANCE
          </OverlayText>
        </OverlayWrapper>
      )}
      {deleting && !deleteSuccessful && (
        <OverlayWrapper>
          <OverlayText>DELETE IN PROGRESS</OverlayText>
        </OverlayWrapper>
      )}
      {deleteSuccessful && (
        <OverlayWrapper>
          <OverlayText error>DELETED! REFRESH TO REMOVE</OverlayText>
        </OverlayWrapper>
      )}
      {!deleting && (!locked || currentUser.type.name === "super") && (
        <CardLink to={link} $deleted={isDeleted ? 1 : 0} $deleting={deleting} />
      )}
      {heading.current?.offsetWidth < heading.current?.scrollWidth && (
        <Marquee $locked={locked || deleting}>
          <Heading>{name?.toUpperCase()}</Heading>&nbsp;
          <Heading>{name?.toUpperCase()}</Heading>
        </Marquee>
      )}
      <TitleWrapper>
        <FacilityName ref={heading}>
          <FacilityAbbr title={name?.toUpperCase()}>{name?.toUpperCase()}</FacilityAbbr>
        </FacilityName>

        <TypeList
          title={typeRows
            .map(t => t.name?.toUpperCase())
            .filter(Boolean)
            .join(" | ")}>
          {typeRows.map((t, index) => {
            const typeStates = (statesBySlug[t.slug] || []).filter(state => allStates?.[state]);
            const isLast = index === typeRows.length - 1;
            return (
              <TypeUnit key={t.slug || t.name}>
                <TypeName title={t.name?.toUpperCase()}>{t.name?.toUpperCase()}</TypeName>
                {typeStates.length > 0 && (
                  <StateCircles>
                    {typeStates.map(state => (
                      <StateCircle
                        key={state}
                        color={`#${allStates[state].color}`}
                        title={`ON ${state}`}>
                        {state.charAt(0).toUpperCase()}
                      </StateCircle>
                    ))}
                  </StateCircles>
                )}
                {!isLast && <TypeSep aria-hidden="true">|</TypeSep>}
              </TypeUnit>
            );
          })}
        </TypeList>

        {region?.name && (
          <Region>
            <FacilityAbbr title={region.name.toUpperCase()}>
              {region.name.toUpperCase()} REGION
            </FacilityAbbr>
          </Region>
        )}
      </TitleWrapper>
      {!isDeleted &&
        roleCanAccessResource("facility_checksheet", "view") &&
        roleCanAccessResource("facility_checksheet_record", "view") &&
        roleCanAccessPage("records") && (
          <IconGrid>
            <GridElem>
              <div>
                {checksheetTotal === 0 && (
                  <Badge offset="10px" color={colors.green}>
                    &#x2713;
                  </Badge>
                )}
                <FontAwesomeIcon icon={faClipboardCheck} className="primaryIcon" />
              </div>
              <CountWrapper>
                <Count hidden={!checksheetsDue || checksheetsDue === 0}>{checksheetsDue} Due</Count>
                <Count hidden={!checksheetDrafts || checksheetDrafts === 0} warning>
                  {checksheetDrafts} Incomplete
                </Count>
                <Count hidden={!checksheetsOverdue || checksheetsOverdue === 0} error>
                  {checksheetsOverdue} Overdue
                </Count>
              </CountWrapper>
            </GridElem>
          </IconGrid>
        )}
      {/* {roleCanAccessResource("event") &&
            eventAlerts?.map(eventType => {
              const {due, draft, overdue, total, icon} = eventType;
              return (
                <GridElem key={`alert-${icon}`}>
                  <div>
                    {total === 0 && (
                      <Badge offset="10px" color={colors.green}>
                        &#x2713;
                      </Badge>
                    )}
                    <FontAwesomeIcon icon={getIcon(icon)} className="primaryIcon" />
                  </div>
                  <CountWrapper>
                    <Count hidden={!due || due === 0}>{due} Due</Count>
                    <Count hidden={!draft || draft === 0} warning>
                      {draft} Incomplete
                    </Count>
                    <Count hidden={!overdue || overdue === 0} error>
                      {overdue} Overdue
                    </Count>
                  </CountWrapper>
                </GridElem>
              );
            })} */}
      {isDeleted && updatedBy && (
        <ArchivedBy>
          Archived by {updatedBy.firstName} {updatedBy.lastName}
          {updatedAt
            ? ` on ${prettyDateInUserTimezone(updatedAt, settings.timezone, "MMM D YYYY, h:mm A")}`
            : ""}
        </ArchivedBy>
      )}
      {!deleting &&
        (roleCanAccessResource("facility", "delete") ||
          roleCanAccessResource("facility", "archive") ||
          roleCanAccessResource("facility", "create")) && (
          <Settings
            type="button"
            title="Facility Settings"
            onClick={() => {
              setTarget(facility);
              setShowModalSettings(true);
            }}>
            {!hasNotifications && (
              <Badge offsetX="7px" offsetY="8px" color={colors.red} diameter="16px">
                <StyledAbbr title="Notifications not configured">
                  <FontAwesomeIcon icon={faExclamation} />
                </StyledAbbr>
              </Badge>
            )}
            <FontAwesomeIcon icon={faCog} className="primaryIcon">
              Settings
            </FontAwesomeIcon>
          </Settings>
        )}
      {!deleting && (
        <Favorite
          type="button"
          onClick={() => {
            setTarget(facility);
            setShowModalFavorite(true);
          }}>
          <FontAwesomeIcon
            icon={favorites?.includes(facility.id) ? faStarFilled : faStar}
            className="primaryIcon">
            Favorite Facility
          </FontAwesomeIcon>
        </Favorite>
      )}
    </Card>
  );
};

FacilityCard.propTypes = {
  facility: PropTypes.objectOf(PropTypes.any).isRequired,
  setTarget: PropTypes.func,
  setShowModalSettings: PropTypes.func,
  setShowModalFavorite: PropTypes.func,
  showArchived: PropTypes.bool,
  favorites: PropTypes.arrayOf(PropTypes.number),
  initialStatesBySlug: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  locked: PropTypes.bool
};

// Style Overrides
const Marquee = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: none;
  animation: ${slideLeft} linear 10s infinite;

  ${Heading} {
    color: ${({theme, $locked}) => ($locked ? theme.secondary : theme.tertiary)};
    padding: ${pad}px;
    white-space: nowrap;
  }
`;

const CardLink = styled(Link)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: ${z("above")};

  ${({$deleted, $deleting}) =>
    $deleted &&
    !$deleting &&
    css`
      background: rgba(0, 0, 0, 0.5);
    `}
`;

const OverlayWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: ${z("above")};
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OverlayText = styled(Text)`
  padding: ${pad}px;
  color: ${({theme}) => theme.warning};

  svg {
    fill: ${({theme}) => theme.warning};
  }
`;

const TitleWrapper = styled.div`
  /* Keep overflow visible so state circle pulse/shadow is not clipped.
     FacilityName and Region handle their own ellipsis truncation. */
  min-width: 0;
`;

const FacilityName = styled(Heading)`
  color: ${({theme}) => theme.secondary};
  width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const FacilityAbbr = styled.abbr`
  text-decoration: none;
`;

const TypeList = styled.div`
  ${flex("row", "wrap", "start", "center")};
  gap: ${pad / 4}px ${pad / 2}px;
  width: 100%;
`;

const TypeUnit = styled.span`
  ${flex("row", "nowrap", "start", "center")};
  gap: ${pad / 4}px;
  max-width: 100%;
`;

const TypeSep = styled.span`
  color: ${({theme}) => theme.primary};
  ${voice.quiet};
  margin-left: ${pad / 4}px;
`;

const TypeName = styled(Small)`
  color: ${({theme}) => theme.primary};
  white-space: nowrap;
  margin-right: 2px;
`;

const StateCircles = styled.div`
  ${flex("row", "nowrap", "start", "center")};
  gap: ${pad / 4}px;
  flex-shrink: 0;
  /* Room for pulse/shadow without shifting layout */
  padding: ${pad / 2}px;
  margin: -${pad / 2}px;
`;

const StateCircle = styled.span`
  ${flex("row", "nowrap", "center", "center")};
  width: 16px;
  height: 16px;
  border-radius: 50%;
  font-size: 9px;
  font-weight: bold;
  line-height: 1;
  text-transform: uppercase;
  color: ${({theme}) => theme.tertiary};
  background-color: ${({color, theme}) => color || theme.error};
  box-shadow: ${shadow};
  position: relative;
  isolation: isolate;
  margin-right: 2px;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${({color, theme}) => color || theme.error};
    border-radius: 50%;
    z-index: -1;
    animation: ${({color, theme}) => pulse(color || theme.error)} 2s infinite;
  }
`;

const Region = styled(Small)`
  color: ${({theme}) => theme.secondary};
  padding-top: ${pad / 2}px;
  width: 100%;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  display: block;
`;

const IconGrid = styled.div`
  ${flex("row", "wrap", "start", "center")};
  margin-top: ${pad * 2}px;
  row-gap: ${pad * 2}px;
`;

const Count = styled(Small)`
  color: ${({theme}) => theme.success};
  color: ${({theme, warning}) => warning && theme.warning};
  color: ${({theme, error}) => error && theme.error};
`;

const CountWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: ${pad}px;
`;

const GridElem = styled(Inline)`
  width: 150px;

  .primaryIcon {
    ${voice.medium};
    font-weight: bold;
    fill: ${({theme}) => theme.secondary};
  }
`;

const Settings = styled(Button)`
  position: absolute;
  bottom: 0;
  right: 0;
  padding: ${pad / 2}px ${pad}px;
  background: transparent;
  z-index: ${z("top")};

  .primaryIcon {
    fill: ${({theme}) => theme.secondary};
  }
`;

const Favorite = styled.button`
  width: inherit;
  display: inherit;
  position: absolute;
  top: 0;
  right: 0;
  padding: ${pad * 1.5}px;
  background: transparent;
  z-index: ${z("top")};

  .primaryIcon {
    ${voice.quiet};
    /* allow a larger click area */
    position: absolute;
    top: 0;
    right: 0;
    padding: ${pad / 2}px;
    fill: ${({theme}) => theme.secondary};
  }
`;

const StyledAbbr = styled(Abbr)`
  padding-left: 0.55px;
`;

const ArchivedBy = styled(Small)`
  color: ${({theme}) => theme.secondary};
  width: calc(100% - 20px);
`;

const Card = styled.div`
  ${flex("column", "nowrap", "space-between")};
  position: relative;
  padding: ${pad}px;
  height: 100%;
  border: ${border} solid ${({theme}) => theme.secondary};
  border-radius: 5px;
  transition: ${transition};
  min-height: 105px;
  overflow: hidden;

  ${({$deleting}) =>
    $deleting &&
    css`
      pointer-events: none;
    `}

  &:hover {
    ${FacilityName} {
      opacity: ${({$showMarquee}) => ($showMarquee ? 0 : 1)};
    }

    ${Marquee} {
      display: inline-flex;
    }

    ${({$locked, theme}) =>
      !$locked &&
      css`
        background-color: ${theme.secondary};

        ${FacilityName} {
          color: ${theme.tertiary};
        }

        ${Text} {
          color: ${theme.tertiary};
        }

        ${Small} {
          color: ${theme.tertiary};
        }

        .primaryIcon {
          fill: ${theme.name === "light" ? theme.primary : theme.tertiary};
        }
      `}
  }
`;

export default FacilityCard;
